/**
 * Shared hook for schedule results tab (baseball, racing, generic).
 * Owns: scheduleId from context, useScheduleResultsQuery, load-more effect, formatTime, getMarketTypeIdsFromResults.
 */
import React from 'react';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { useSchedulePanelContext } from 'features/events/SchedulePanelContext';
import { useScheduleResultsQuery, useSchedulesQuery } from 'features/events/queries';

const RACING_SPORT_IDS = [10, 11, 20, 21, 30];

export const NON_MARKET_KEYS = new Set([
  'event_id',
  'event_timestamp',
  'sport_id',
  'description',
  'week',
  'home_team_code',
  'away_team_code',
  'home_score',
  'away_score',
  'home_score_1ht',
  'away_score_1ht',
]);

export function formatTime(ts) {
  if (ts == null) return '';
  const d = new Date(Number(ts) * 1000);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

export function getMarketTypeIdsFromResults(results) {
  const set = new Set();
  for (const row of results) {
    for (const key of Object.keys(row)) {
      if (NON_MARKET_KEYS.has(key)) continue;
      const num = parseInt(key, 10);
      if (Number.isFinite(num)) set.add(num);
    }
  }
  return Array.from(set).sort((a, b) => a - b);
}

function isRacingSport(sportId) {
  const id = sportId != null ? Number(sportId) : null;
  return id != null && RACING_SPORT_IDS.includes(id);
}

export function useScheduleResultsTab(sportId) {
  const panelCtx = useSchedulePanelContext();
  const storeScheduleId = useScheduleContext().currentScheduleId;
  const scheduleId = panelCtx?.scheduleId ?? storeScheduleId;
  const scheduleIdNormalized = scheduleId != null ? String(scheduleId) : null;

  const {
    data: results = [],
    isLoading,
    isError,
    refetch,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useScheduleResultsQuery({ scheduleId: scheduleIdNormalized });

  const { data: schedules = [] } = useSchedulesQuery();

  const resultsWithId = results.filter((row) => row.event_id != null);

  const n_runners = React.useMemo(() => {
    if (!isRacingSport(sportId)) return undefined;
    const currentSchedule = Array.isArray(schedules)
      ? schedules.find((s) => String(s.schedule_id) === String(scheduleIdNormalized))
      : null;
    const n = currentSchedule?.sport?.n_runners != null
      ? parseInt(String(currentSchedule.sport.n_runners), 10)
      : 6;
    return Number.isFinite(n) ? n : 6;
  }, [sportId, schedules, scheduleIdNormalized]);

  const loadMoreSentinelRef = React.useRef(null);
  const fetchNextPageRef = React.useRef(fetchNextPage);
  const wasIntersectingRef = React.useRef(false);
  const didSkipInitialIntersectingRef = React.useRef(false);
  fetchNextPageRef.current = fetchNextPage;

  React.useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    let scrollRoot = null;
    let node = el.parentElement;
    while (node && node !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(node);
      if (['auto', 'scroll', 'overlay'].includes(overflow) || ['auto', 'scroll', 'overlay'].includes(overflowY)) {
        scrollRoot = node;
        break;
      }
      node = node.parentElement;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const isIntersecting = !!entry?.isIntersecting;
        if (isIntersecting) {
          if (!didSkipInitialIntersectingRef.current) {
            didSkipInitialIntersectingRef.current = true;
            wasIntersectingRef.current = true;
            return;
          }
          if (!wasIntersectingRef.current) {
            wasIntersectingRef.current = true;
            fetchNextPageRef.current();
          }
        } else {
          wasIntersectingRef.current = false;
        }
      },
      { root: scrollRoot, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage]);

  return {
    results,
    resultsWithId,
    isLoading,
    isError,
    refetch,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loadMoreSentinelRef,
    scheduleId: scheduleIdNormalized,
    n_runners,
  };
}
